import axios from 'axios';
import api from './api';
import type { SizeUnit } from '../types/venue';
import type { InventoryItemRecipe } from '../types/execution';

/**
 * Phase 7.A.2b — InventoryItemRecipe authoring (sale-time consumption recipe).
 *
 * Backend mounted at /admin/venue/:venueId/inventory/item/:itemId/recipe
 *   GET    → { recipe: InventoryItemRecipe | null }
 *   PUT    → { recipe } (200 replaced / 201 created)  — VENUE_OPS_ROLES
 *   DELETE → { recipe } (deactivates active recipe)    — VENUE_OPS_ROLES
 *
 * The editor sends the operator INPUT form only (inputQuantity + inputUnit).
 * The backend reuses the ERP converter to compute the canonical stock-unit
 * `quantity` and echoes back the input snapshot. The frontend never computes
 * canonical quantities itself.
 */

const base = (venueId: string, itemId: string) =>
  `/admin/venue/${venueId}/inventory/item/${itemId}/recipe`;

export interface RecipeComponentInput {
  inventoryItemId: string;
  /** Operator-entered amount, e.g. 150 for "150 ml" or 0.2 for "0.2 unit". */
  inputQuantity: number;
  /** 'unit' = direct stock fraction (no size conversion). */
  inputUnit: SizeUnit;
  notes?: string;
  position?: number;
}

export interface PutRecipePayload {
  components: RecipeComponentInput[];
}

const inventoryItemRecipeService = {
  /** Returns the active recipe for the item, or null when none exists. */
  getItemRecipe: async (venueId: string, itemId: string): Promise<InventoryItemRecipe | null> => {
    const response = await api.get(base(venueId, itemId));
    return response.data?.recipe ?? null;
  },

  /** Replace/create the active recipe. Returns the persisted recipe. */
  putItemRecipe: async (
    venueId: string,
    itemId: string,
    payload: PutRecipePayload,
  ): Promise<InventoryItemRecipe> => {
    const response = await api.put(base(venueId, itemId), payload);
    return response.data?.recipe;
  },

  /** Deactivate the active recipe. Returns the deactivated recipe. */
  deleteItemRecipe: async (venueId: string, itemId: string): Promise<InventoryItemRecipe> => {
    const response = await api.delete(base(venueId, itemId));
    return response.data?.recipe;
  },
};

/** Friendly per-code messages for the recipe authoring endpoint. */
const RECIPE_ERROR_MESSAGES: Record<string, string> = {
  COMPONENT_NOT_STOCK_BEARING:
    'That item is itself a recipe item — pick a stock item, such as a bottle, bag or piece.',
  COMPONENT_HAS_NO_SIZE:
    'This item has no size set, so ml/g cannot be converted. Set its size or use unit.',
  UNIT_FAMILY_MISMATCH:
    'The unit does not match the item. For example, ml cannot be used for a kg item.',
  QUANTITY_TOO_SMALL: 'That amount rounds to zero stock. Increase it.',
  RECIPE_CONFLICT: 'A recipe already exists. Reload and try again.',
};

/**
 * Map a recipe-endpoint error to an operator message. Prefers a friendly
 * per-code message, then the backend's own (already component-indexed)
 * message, then HTTP-status fallbacks.
 */
export function extractRecipeError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    const errorObj = data?.['error'] as Record<string, unknown> | undefined;
    const code = errorObj?.['code'];
    if (typeof code === 'string' && RECIPE_ERROR_MESSAGES[code]) {
      return RECIPE_ERROR_MESSAGES[code];
    }
    if (err.response?.status === 403) {
      return 'You do not have permission to edit recipes.';
    }
    const message = errorObj?.['message'] ?? data?.['message'];
    if (typeof message === 'string' && message) return message;
    if (err.response?.status === 404) return 'Inventory item or recipe not found.';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Could not save the recipe. Please try again.';
}

export default inventoryItemRecipeService;
